import { type FC, useMemo } from "react";
import { Icon } from "@onshopfront/components";
import { addIndefiniteArticle, pluralize, uppercaseFirst } from "#/Utilities/Generic/Strings.ts";
import { UPLOAD_TYPES } from "#/Workers/Upload/UploadEvents.ts";
import { assertNever } from "../../../../types/internal/utility.ts";

interface Props {
    type: UPLOAD_TYPES;
    count: number;
    onDismiss: () => void;
}

function formatTypeString(string: string, count: number): string {
    return uppercaseFirst(addIndefiniteArticle(pluralize(string, count), count));
}

function getTypeText(type: UPLOAD_TYPES, count: number): string {
    switch(type) {
        case UPLOAD_TYPES.SALE:
            return formatTypeString("sale", count);
        case UPLOAD_TYPES.UPDATE_SALE:
            return formatTypeString("sale update", count);
        case UPLOAD_TYPES.BARCODE:
            return formatTypeString("barcode association", count);
        case UPLOAD_TYPES.OPEN_REGISTER:
            return formatTypeString("open register", count);
        case UPLOAD_TYPES.CUSTOMER:
            return formatTypeString("customer", count);
        case UPLOAD_TYPES.UPDATE_CUSTOMER:
            return formatTypeString("customer update", count);
        case UPLOAD_TYPES.EMAIL_RECEIPT:
            return formatTypeString("email receipt", count);
        case UPLOAD_TYPES.ACTION:
            return formatTypeString("action", count);
        case UPLOAD_TYPES.TAKINGS:
            return formatTypeString("taking", count);
        case UPLOAD_TYPES.VOID_SALE:
            return formatTypeString("cancelled sale", count);
        case UPLOAD_TYPES.UNPARK_SALE:
            return formatTypeString("unpark sale", count);
        case UPLOAD_TYPES.CUSTOMER_PAYMENT:
            return formatTypeString("customer payment", count);
        case UPLOAD_TYPES.EMAIL_COMPONENT:
            return formatTypeString("email", count);
        case UPLOAD_TYPES.CLOSE_REGISTER:
            return formatTypeString("register closure", count);
        case UPLOAD_TYPES.STOCKTAKE_COUNT:
            return formatTypeString("stocktake count", count);
        case UPLOAD_TYPES.ERROR:
            return formatTypeString("error", count);
        case UPLOAD_TYPES.SECURITY_EVENT:
            return formatTypeString("security event", count);
        case UPLOAD_TYPES.KITCHEN_ORDER:
            return formatTypeString("kitchen order", count);
        default:
            return assertNever(type);
    }
}

export const SynchroniseError: FC<Props> = ({
    type,
    count,
    onDismiss,
}) => {
    const text = useMemo(() => {
        let result = getTypeText(type, count) + " failed to";

        if(type === "email-receipt" || type === "email-component") {
            result += " send";
        } else {
            result += " upload";
        }

        return result;
    }, [ type, count ]);

    return (
        <div className="synchronise-error">
            <div className="error-icon">
                <Icon
                    icon={[ "fal", "exclamation" ]}
                    className="mr-2"
                />
            </div>
            <div className="error-text">
                {text}
            </div>
            <div
                className="dismiss-button"
                onClick={e => {
                    e.stopPropagation();

                    onDismiss();
                }}
            >
                <Icon icon={[ "fal", "times-circle" ]} />
            </div>
        </div>
    );
};
